import { Box, Button, CircularProgress, List, ListItem, Typography } from '@mui/material';
import { useTheme } from "@mui/material/styles";
import { Ico_Export, Ico_Filter_Active_Component, Ico_Filter_Component, Ico_SortAscending, Ico_SortDescending, TypedPageTitle, useSnackbar } from '@sagecentral/core-ui';
import { ITask, TaskFilter } from "models/tasks";
import { Sort } from 'models/grids';
import { useDensityHandler, useGridFilters, useGridMetadata, useGridSorting, usePersistedPageSize, useResizeHandler } from 'hooks/shared';
import { useTaskExport, useTaskFieldUpdater, useTaskPermissions, useTasks } from 'hooks/tasks';
import TabsHeader from './TabsHeader';
import useTaskColumns from './useTaskColumns';
import { GridBox, GridToolbar, GridNoRowsOverlay, CentralDataGridPro } from 'components/shared/grids';
import { PreferenceType } from 'components/preferences';
import { usePreference } from 'hooks/preferences';
import { useGridApiRef } from '@mui/x-data-grid-pro';
import BulkUpdateMenuButton from 'components/shared/bulk-update/BulkUpdateMenuButton';
import { CustomCircularProgress } from 'components/shared/layout';
import { formatDate } from 'utils/date-utils';
import { customLocaleText } from 'models/grids/HeaderFilterLocaleText';
import { Helmet } from "react-helmet";
import { toPascalCase } from 'utils/string-utils';
import { useState } from 'react';

interface ITaskGridProps {
    all?: boolean,
    Status: TaskFilter
};

const NoRowsOverlay = (status: TaskFilter) => {
    if (status == 'all') {
        return (
            <GridNoRowsOverlay text={`No tasks found.`} />
        );
    }
    else {
        return (
            <GridNoRowsOverlay text={`No ${status.replace("-", " ")} tasks found.`} />
        );
    }
};

const TasksGrid = (props: ITaskGridProps) => {
    const { Status } = props;
    const theme = useTheme();
    const apiRef = useGridApiRef();
    const { displaySnackbar } = useSnackbar();
    const permissions = useTaskPermissions();
    const [refreshTabsCount, setRefreshTabsCount] = useState(false);

    const defaultSort: Sort = {
        field: 'endDate',
        dir: 'asc'
    };

    const [preferencesLoaded, settings, setSettings] = usePreference(PreferenceType.ProjectTasksGrid);


    const [sort, handleSortChange, sortModel] = useGridSorting({
        defaultSort: [defaultSort],
        settings,
        setSettings
    });

    const [filter, handleFilterChange, filterModel] = useGridFilters({
        settings,
        setSettings
    });

    const [density, handleDensityChange] = useDensityHandler({
        settings,
        setSettings
    });

    const [pagination, handlePaginationModelChange] = usePersistedPageSize({
        settings,
        setSettings
    });

    const gridMetadata = useGridMetadata(filter, sort, settings?.gridSettings, pagination);

    const [result, total, loading, error, forceRefresh] = useTasks(Status, gridMetadata, preferencesLoaded);

    const [columnsModel, visibilityModel, handleColumnVisibilityOrOrderChange] = useTaskColumns({
        status: Status,
        hasViewProblemLogPrivileges: permissions?.canViewProblemLog ?? false,
        privilegesLoading: loading,
        settings,
        setSettings
    });

    const width = useResizeHandler(() => (result?.length > 0) || false, [result]);
    const [handleExport, exportWorking] = useTaskExport(Status, gridMetadata, visibilityModel, columnsModel);

    const isFilterApplied = () => ((gridMetadata.filterParam?.filters?.length || 0) > 0);
    const getData = () => (loading ? [] : result);

    const [saveStatus] = useTaskFieldUpdater('status',
        () => displaySnackbar('success', 'Task Status changed successfully.'),
        (error) => displaySnackbar('warning', error)
    );

    const [saveEndDate] = useTaskFieldUpdater('end-date',
        () => displaySnackbar('success', 'Task End Date changed successfully.'),
        (error) => displaySnackbar('warning', error)
    );

    const handleRowUpdate = async (newRow: ITask, oldRow: ITask) =>  {        
        if (oldRow.statusId !== newRow.statusId) {
            const success = await saveStatus(newRow.statusId, newRow.id);
            if (success) {
                oldRow = {
                    ...oldRow,
                    statusId: newRow.statusId
                }
                forceRefresh();
                setRefreshTabsCount(prev => !prev);
                return { ...oldRow, _action: 'delete'};
            }           
        }
        else if(formatDate(oldRow.endDate.toString()) !== formatDate(newRow.endDate.toString())){
            
            const success = await saveEndDate(formatDate(newRow.endDate.toString()), newRow.id);
            if (success) {
                oldRow = {
                    ...oldRow,
                    endDate: newRow.endDate
                }
            }          
        }          
        
        return oldRow
    };        

    return (
        <Box>
            <Helmet>
                <title>Sage Central | My Tasks | {toPascalCase(props.Status)}</title>
            </Helmet>
            <Box sx={{ display: "flex" }} component="section" aria-label='tasks header section'>
                <TypedPageTitle title="My Tasks" sx={{ color: '#001A69' }}></TypedPageTitle>
                <Box sx={{ marginLeft: 'auto', display: 'flex', height: 'fit-content', gap: '14px' }}>
                    {(permissions?.canChangeStatus || permissions?.canChangeResources) &&
                        <BulkUpdateMenuButton child={
                            <List>
                                {permissions?.canChangeResources &&
                                    <ListItem>
                                        <Button href={`${process.env.REACT_APP_CLASSIC_URL}/BulkUpdateTasks/BulkUpdateTasks?area=WorkflowEnduser&mode=Resource`} >Update Tasks Resources</Button>
                                    </ListItem>
                                }
                                {permissions?.canChangeStatus &&
                                    <ListItem>
                                        <Button href={`${process.env.REACT_APP_CLASSIC_URL}/BulkUpdateTasks/BulkUpdateTasks?area=WorkflowEnduser&mode=Status`} >Update Tasks Status</Button>
                                    </ListItem>
                                }
                            </List>
                        } />
                    }
                    {exportWorking ? <CircularProgress sx={{ top: '0%', left: '0%' }} /> :
                        <Typography sx={{ display: 'flex', gap: '8px', color: theme.palette.common.black, cursor: 'pointer' }} 
                            onClick={handleExport} 
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    handleExport();
                                }
                            }}
                            rel="noopener noreferrer"
                            tabIndex={0}
                            aria-label="Export tasks">
                            <svg fill="none" height={24} width={24}>{Ico_Export}</svg> Export
                        </Typography>
                    }
                </Box>
            </Box>
            <TabsHeader refreshTabsCount={refreshTabsCount} />

            <GridBox getData={getData}>
                {preferencesLoaded && pagination && <Box sx={{
                    maxHeight: "75vh",
                    width: "100%",
                    display: 'flex',
                    flexDirection: 'column'
                }}  
                component="section"
                aria-label="grid section">
                    <CentralDataGridPro
                        sx={{
                            width: '100%'
                        }}
                        apiRef={apiRef}
                        rows={getData()}
                        rowCount={total}
                        columns={columnsModel}
                        getRowId={(r) => r.id}
                        filterMode="server"
                        filterModel={filterModel}
                        sortingMode="server"
                        sortModel={sortModel}
                        paginationMode='server'
                        pagination={true}
                        pageSizeOptions={[25, 50, 75, 100]}
                        paginationModel={pagination}
                        density={density ?? 'standard'}
                        initialState={{
                            columns: {
                                columnVisibilityModel: visibilityModel,
                            }
                        }}
                        onSortModelChange={handleSortChange}
                        onFilterModelChange={handleFilterChange}
                        onColumnVisibilityModelChange={() => handleColumnVisibilityOrOrderChange(apiRef)}
                        onColumnWidthChange={() => handleColumnVisibilityOrOrderChange(apiRef)}
                        onDensityChange={(newDensity) => handleDensityChange(newDensity)}
                        onColumnOrderChange={() => handleColumnVisibilityOrOrderChange(apiRef)}
                        onPaginationModelChange={handlePaginationModelChange}
                        processRowUpdate={handleRowUpdate}
                        disableDensitySelector={true}
                        slots={{
                            loadingOverlay: () => (<CircularProgress/>),
                            columnMenuSortAscendingIcon: Ico_SortAscending,
                            columnMenuSortDescendingIcon: Ico_SortDescending,
                            columnSortedAscendingIcon: Ico_SortAscending,
                            columnSortedDescendingIcon: Ico_SortDescending,
                            columnUnsortedIcon: undefined,
                            openFilterButtonIcon: isFilterApplied() ? Ico_Filter_Active_Component : Ico_Filter_Component,
                            quickFilterIcon: Ico_Filter_Component,
                            columnFilteredIcon: Ico_Filter_Component,
                            quickFilterClearIcon: Ico_Filter_Component,
                            columnMenuFilterIcon: Ico_Filter_Component,
                            noRowsOverlay: () => NoRowsOverlay(Status),
                            toolbar: GridToolbar
                            // headerFilterMenu: null
                        }}
                        headerFilters
                        localeText={customLocaleText}
                        loading={loading || !result}
                        getRowClassName={(params) =>
                            params.indexRelativeToCurrentPage % 2 === 0 ? 'row even' : 'row odd'
                        }
                        getRowHeight={() => 'auto'}                   
                    />
                </Box> || <CustomCircularProgress />}
            </GridBox>
        </Box>
    )
};

export default TasksGrid;